import api from "../../../lib/api";

/* Cross-device Residence sync.
   Backend (residence_state collection) is the authoritative persisted store.
   localStorage remains the fast working cache / offline fallback.
   Saves are debounced per slice and flushed with sendBeacon on page hide. */

const SAVE_DELAY = 900;
const cache = new Map();   // householdId -> { promise, slices, overrides }
const pending = new Map(); // `${hh}::${slice}` -> { timer, householdId, sliceName, data }

export function getActiveHouseholdId() {
  try { return window.localStorage.getItem("arawealth-household") || null; } catch { return null; }
}

export function loadResidenceState(householdId) {
  if (!householdId) return Promise.resolve(null);
  let entry = cache.get(householdId);
  if (!entry) {
    entry = { slices: null, overrides: {}, promise: null };
    cache.set(householdId, entry);
    entry.promise = api
      .get(`/api/households/${householdId}/residence-state`)
      .then((res) => (res.ok ? res.json() : null))
      .then((body) => {
        if (!body || !body.slices) { cache.delete(householdId); return null; }
        entry.slices = { ...body.slices, ...entry.overrides };
        return entry.slices;
      })
      .catch(() => { cache.delete(householdId); return null; });
  }
  return entry.promise;
}

function pushSlice(householdId, sliceName, data) {
  return api
    .put(`/api/households/${householdId}/residence-state/${sliceName}`, { data })
    .catch(() => { /* fail calmly — localStorage cache still holds the data */ });
}

export function saveSlice(householdId, sliceName, data) {
  if (!householdId || typeof window === "undefined") return;
  const entry = cache.get(householdId);
  if (entry) {
    entry.overrides[sliceName] = { data, updated_at: new Date().toISOString() };
    if (entry.slices) entry.slices[sliceName] = entry.overrides[sliceName];
  }
  const key = `${householdId}::${sliceName}`;
  const existing = pending.get(key);
  if (existing) clearTimeout(existing.timer);
  pending.set(key, {
    householdId,
    sliceName,
    data,
    timer: setTimeout(() => {
      pending.delete(key);
      pushSlice(householdId, sliceName, data);
    }, SAVE_DELAY),
  });
}

export function cancelPendingSave(householdId, sliceName) {
  const key = `${householdId}::${sliceName}`;
  const existing = pending.get(key);
  if (existing) {
    clearTimeout(existing.timer);
    pending.delete(key);
  }
}

function flushPending() {
  pending.forEach(({ timer, householdId, sliceName, data }, key) => {
    clearTimeout(timer);
    pending.delete(key);
    try {
      const blob = new Blob([JSON.stringify({ data })], { type: "application/json" });
      const sent = navigator.sendBeacon
        && navigator.sendBeacon(`/api/households/${householdId}/residence-state/${sliceName}`, blob);
      if (!sent) pushSlice(householdId, sliceName, data);
    } catch {
      pushSlice(householdId, sliceName, data);
    }
  });
}

if (typeof window !== "undefined") {
  window.addEventListener("pagehide", flushPending);
}
